import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import type { UserListItem } from '@avisoma/shared';
import { SortableTable } from '../../components/SortableTable';
import { formatDateTime } from '../../lib/dateFormat';
import { useAuth } from '../../hooks/useAuth';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../catalyst/dropdown';
import { Badge } from '../../catalyst/badge';
import type { ComponentProps } from 'react';

type BadgeColor = ComponentProps<typeof Badge>['color'];

const ROLE_COLOR: Record<string, BadgeColor> = {
  admin: 'red',
  supervisor: 'purple',
  user: 'blue',
  curator: 'emerald',
};

function RoleBadge({ role, deactivatedAt }: { role: string; deactivatedAt: string | null }) {
  return (
    <div className={`flex items-center gap-1.5 ${deactivatedAt ? 'opacity-60' : ''}`}>
      <Badge color={ROLE_COLOR[role] ?? 'zinc'}>{role}</Badge>
      {deactivatedAt && (
        <Badge color="zinc" title={`Deactivated ${formatDateTime(deactivatedAt)}`}>
          Deactivated
        </Badge>
      )}
    </div>
  );
}

export function UsersTable({
  users,
  onEditRole,
  onResetPassword,
  onDelete,
  onDeactivate,
  onReactivate,
}: {
  users: UserListItem[];
  onEditRole: (u: UserListItem) => void;
  onResetPassword: (u: UserListItem) => void;
  onDelete: (u: UserListItem) => void;
  onDeactivate: (u: UserListItem) => void;
  onReactivate: (u: UserListItem) => void;
}) {
  const auth = useAuth();
  const currentUserId = auth.user?.id ?? null;

  return (
    <div className="overflow-hidden rounded-lg">
      <SortableTable
        storageId="users"
        limitHeight={false}
        data={users}
        columns={[
          {
            accessor: 'username',
            label: 'Username',
            render: (row) => (
              <span
                className={`font-medium text-zinc-900 dark:text-zinc-100 ${
                  row.deactivatedAt ? 'opacity-60' : ''
                }`}
              >
                {row.username}
              </span>
            ),
          },
          {
            accessor: 'role',
            label: 'Role',
            render: (row) => <RoleBadge role={row.role} deactivatedAt={row.deactivatedAt} />,
          },
          {
            accessor: 'createdAt',
            label: 'Created',
            render: (row) => (
              <span className={row.deactivatedAt ? 'opacity-60' : ''}>{formatDateTime(row.createdAt)}</span>
            ),
          },
          {
            accessor: 'lastLoginAt',
            label: 'Last login',
            render: (row) => (
              <span className={row.deactivatedAt ? 'opacity-60' : ''}>
                {row.lastLoginAt ? formatDateTime(row.lastLoginAt) : <span className="text-zinc-500">never</span>}
              </span>
            ),
          },
          {
            accessor: 'id',
            label: '',
            render: (row) => {
              const isSelf = row.id === currentUserId;
              const isDeactivated = row.deactivatedAt !== null;
              return (
                <Dropdown>
                  <DropdownButton plain aria-label="Row actions">
                    <EllipsisVerticalIcon className="size-5" />
                  </DropdownButton>
                  <DropdownMenu anchor="bottom end">
                    {isDeactivated ? (
                      <DropdownItem onClick={() => onReactivate(row)}>
                        <DropdownLabel>Reactivate</DropdownLabel>
                      </DropdownItem>
                    ) : (
                      <>
                        <DropdownItem onClick={() => onEditRole(row)}>
                          <DropdownLabel>Edit role</DropdownLabel>
                        </DropdownItem>
                        <DropdownItem onClick={() => onResetPassword(row)}>
                          <DropdownLabel>Reset password</DropdownLabel>
                        </DropdownItem>
                        <DropdownDivider />
                        <DropdownItem
                          disabled={isSelf}
                          title={isSelf ? 'You cannot deactivate your own account.' : undefined}
                          onClick={() => onDeactivate(row)}
                        >
                          <DropdownLabel className="text-red-600 dark:text-red-400">Deactivate</DropdownLabel>
                        </DropdownItem>
                      </>
                    )}
                    <DropdownDivider />
                    <DropdownItem
                      disabled={isSelf}
                      title={isSelf ? 'You cannot delete your own account.' : undefined}
                      onClick={() => onDelete(row)}
                    >
                      <DropdownLabel className="text-red-600 dark:text-red-400">Delete</DropdownLabel>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              );
            },
          },
        ]}
        rowKey={(row) => row.id}
      />
    </div>
  );
}
